import { STORAGE_KEYS } from './constants';

// Bump when a stored data shape changes, and add a migration step in
// migrateAndValidate (payloads) and migrateLocalStorage (bgt_* keys).
export const DATA_VERSION = 1;

const COLLECTIONS = [
  'categories', 'budgetItems', 'transactions', 'buckets',
  'yearlyExpenses', 'currencyBalances', 'recurringTemplates',
];

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Validate an incoming payload (JSON import, file sync, Drive sync) and
 * upgrade it to DATA_VERSION. Throws an Error with a user-showable message
 * on anything that isn't a budget payload. Returns the migrated copy.
 */
export function migrateAndValidate(data) {
  if (!isPlainObject(data)) throw new Error('not a budget backup file');
  if ((data.version || 1) > DATA_VERSION) {
    throw new Error('created by a newer app version — update the app first');
  }
  if (data.settings === undefined && !COLLECTIONS.some(k => data[k] !== undefined)) {
    throw new Error('not a budget backup file');
  }
  if (data.settings !== undefined && !isPlainObject(data.settings)) {
    throw new Error('invalid "settings" section');
  }
  for (const key of COLLECTIONS) {
    if (data[key] !== undefined && !Array.isArray(data[key])) {
      throw new Error(`invalid "${key}" section`);
    }
  }
  // Migration steps go here as DATA_VERSION grows:
  // if ((data.version || 1) < 2) data = { ...data, /* reshape */ };
  return { ...data, version: DATA_VERSION };
}

/**
 * Validate + migrate a payload, then apply it into the state setters.
 * Validation happens before any setter runs, so a throw leaves state untouched.
 */
export function applyPayload(rawData, setters) {
  const data = migrateAndValidate(rawData);
  if (data.settings)            setters.setSettings(data.settings);
  if (data.categories)          setters.setCategories(data.categories);
  if (data.budgetItems)         setters.setBudgetItems(data.budgetItems);
  if (data.transactions)        setters.setTransactions(data.transactions);
  if (data.buckets)             setters.setBuckets(data.buckets);
  if (data.yearlyExpenses)      setters.setYearlyExpenses(data.yearlyExpenses);
  if (data.currencyBalances)    setters.setCurrencyBalances(data.currencyBalances);
  if (data.recurringTemplates)  setters.setRecurringTemplates(data.recurringTemplates);
  return data;
}

/**
 * Upgrade data already sitting in localStorage after an app update.
 * Called once from index.js before React renders. A missing version key
 * means version 1 (all data written before versioning existed).
 */
export function migrateLocalStorage() {
  let stored = 1;
  try { stored = parseInt(localStorage.getItem(STORAGE_KEYS.dataVersion), 10) || 1; } catch {}
  if (stored === DATA_VERSION) return;
  // Per-key migration steps go here as DATA_VERSION grows:
  // if (stored < 2) { /* rewrite affected bgt_* keys */ }
  try { localStorage.setItem(STORAGE_KEYS.dataVersion, String(DATA_VERSION)); } catch {}
}
